import { removeUnderscores } from './inputFormatters'
import {
  validContributions,
  SearchAccountsResponsePayload,
  AchRelationshipResponsePayload,
  DistributionReasonInfo
} from '@Types'
import isNil from 'lodash.isnil'
import isEmpty from 'lodash.isempty'
import toNumber from 'lodash/toNumber'

export const NONE_VALUE = 'None'
export const ACH_VALUE: string = 'ach'
export const CHECK_VALUE: string = 'check'
export const CASH_JOURNAL_VALUE: string = 'cj'
export const WIRE_VALUE: string = 'wire'
export const POSITION_JOURNAL_VALUE: string = 'pj'
export const EMPTY_VALUE: string = 'empty'

export const achTypeOption = {
  key: 'ach-transfer',
  text: 'ACH',
  value: ACH_VALUE
}

export const cashJournalTypeOption = {
  key: 'cash-journal',
  text: 'Cash Journal',
  value: CASH_JOURNAL_VALUE
}

export const wireTypeOption = {
  key: 'wire-transfer',
  text: 'Wire',
  value: WIRE_VALUE
}

export const checkTypeOption = {
  key: 'check-transfer',
  text: 'Check',
  value: CHECK_VALUE
}

export const positionJournalTypeOption = {
  key: 'position-journal-transfer',
  text: 'Position Journal',
  value: POSITION_JOURNAL_VALUE
}

export const emptyTransferTypeOption = {
  key: '',
  text: 'Select a transfer type...',
  value: EMPTY_VALUE
}

export const buildIraContributionOptions = (types: validContributions[]) => {
  const contributionTypes = types.map((type: validContributions) => ({
    key: `contribution-type-${type?.reason}`,
    text: type?.reason,
    value: type?.reason
  }))

  return [...contributionTypes]
}

export const buildIraDistributionOptions = (types: DistributionReasonInfo[]) => {
  const distributionReasons = types.map((type: DistributionReasonInfo) => ({
    key: `distribution-reason-${type?.reason}`,
    text: removeUnderscores(type?.reason),
    value: type?.reason
  }))

  return [...distributionReasons]
}

export const buildAccountText = (acctNum?: string, title?: string) => {
  return `${acctNum} ${title && `(${title})`}`
}

export const buildAccountOption = (
  account: SearchAccountsResponsePayload | null | undefined,
  numberFormatter: any,
  amountAvailable: number
) => {
  const amtAvail = ` - ${numberFormatter(amountAvailable, {
    style: 'currency',
    currency: 'USD'
  })}`
  return {
    key: `apex-account-${account?.accountNumber}`,
    text: `${buildAccountText(account?.accountNumber, account?.accountTitle)}${amtAvail}`,
    value: account?.accountNumber
  }
}

export const buildAchRelationshipText = (achRel: AchRelationshipResponsePayload) => {
  return `${achRel?.bankAccount} ${achRel?.nickname || ''}`
}

export const buildAchRelationshipOptions = (achRelationships: AchRelationshipResponsePayload[]) => {
  return achRelationships.map((achRel: AchRelationshipResponsePayload) => ({
    key: `ach-rel-${achRel.id}`,
    text: buildAchRelationshipText(achRel),
    value: achRel.id
  }))
}

export const buildACHAccountOptions = (
  achRelationships: AchRelationshipResponsePayload[] | null = null,
  acctOption: {
    key: string
    value?: string
    text: string
  }
) => {
  let options = null
  if (!isNil(achRelationships) && !isEmpty(achRelationships)) {
    const items = buildAchRelationshipOptions(achRelationships)
    options = {
      from: [acctOption, ...items],
      to: [acctOption, ...items]
    }
  }

  return options
}

export const buildMoneyFormat = (amount: number | string): string => {
  const makeNumber = toNumber(amount)
  if (!isNaN(makeNumber)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(makeNumber)
  }

  return 'No amount entered'
}

export const buildAccountDisplayText = (amountAvailable: number, acctNum?: string, title?: string) => {
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amountAvailable)
  return `${acctNum} ${title && `(${title}) - ${amount}`}`
}

export const buildToAccountDisplayText = (accountNumber: string, accountTitle: string, errorMessage: string) => {
  if (!isNil(accountNumber) && !isNil(accountTitle)) {
    return `${accountNumber} (${accountTitle})`
  }
  return errorMessage || ' '
}
